import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Pressable } from 'react-native'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'

import { skipToken } from '@reduxjs/toolkit/query'
import { uniqueId } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  ProviderSelfHarmTaskArguments,
  ProviderTaskType,
  ProviderTriageRiskTaskArguments,
} from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { CheckIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CheckIcon'
import { ChevronIcon, ChevronIconDirection } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ChevronIcon'
import { Popover } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/popover/Popover'
import {
  ProfileCardBadge,
  RISK_BADGE_TEXT,
  SELF_HARM_BADGE_TEXT,
} from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/profileCardBadge/ProfileCardBadge'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'

import { MarkRiskAddressedModal } from '../../../dashboard/openItems/components/MarkRiskAddressedModal'
import RiskAlertPopover, { RiskAlertPopoverAlert } from '../../../dashboard/openItems/components/RiskAlertPopover'
import { useMarkRiskAddressed } from '../../../dashboard/openItems/hooks/useMarkRiskAddressed'
import { useNavigateToGeneralNote } from '../../../dashboard/openItems/hooks/useNavigateToGeneralNote'
import { useRiskAlertPopoverData } from '../../../dashboard/openItems/hooks/useRiskAlertPopover'
import { parseTaskDate } from '../../../dashboard/openItems/utils'
import { getAuthUserId } from '../../../data/auth/authSelectors'
import { useAppDispatch } from '../../../data/store'
import { useIsEngageDAProvider } from '../../../hooks/useIsEngageDAProvider'
import useClientRiskStatus, {
  ClientRiskEntry,
} from '../../../lyraTherapy/clients/clientList/v3/hooks/useClientRiskStatus'
import { setToastContent } from '../../../lyraTherapy/data/ltToastAutoActions'
import {
  useDeactivateRiskFlagMutation,
  useGetClientEpisodesQuery,
  useGetRiskFlagQuery,
} from '../../../lyraTherapy/episodes/data/episodesApi'
import { getSafetyPlanRoute, useClientSafetyPlan } from '../../../lyraTherapy/safetyPlan/data/useClientSafetyPlan'
import { CLIENT_RISK_SUMMARY } from '../../constants/routingConstants'

export type RiskBadgeClient = {
  id: string
  nextAppointment?: { episodeId?: string | null } | null
  lastAppointment?: { episodeId?: string | null } | null
}

type RiskBadgeWithPopoverProps = {
  riskEntry: ClientRiskEntry
  client: RiskBadgeClient
  logNoteSource?: string
  isClientChartVersion?: boolean
}

const RiskBadgeContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['4px'],
  paddingHorizontal: theme.spacing['8px'],
  paddingVertical: theme.spacing['2px'],
  borderRadius: 4,
  backgroundColor: theme.colors.backgroundError,
}))

const AddressedLabel = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['4px'],
}))

const badgeTextStyle = { textTransform: 'uppercase' as const }

export type RiskBadgeForClientProps = {
  clientId: string
  logNoteSource?: string
  showRiskBadge?: boolean
  isClientChartVersion?: boolean
}

export const RiskBadgeForClient: React.FC<RiskBadgeForClientProps> = ({
  clientId,
  logNoteSource,
  showRiskBadge,
  isClientChartVersion = false,
}) => {
  const isDAProvider = useIsEngageDAProvider()
  const clientRiskStatus = useClientRiskStatus()
  const riskEntry = clientRiskStatus.get(clientId) ?? null

  const showPopoverBadge = isDAProvider || (isClientChartVersion && riskEntry?.status === 'at_risk')

  if (showPopoverBadge && riskEntry) {
    return (
      <RiskBadgeWithPopover
        riskEntry={riskEntry}
        client={{ id: clientId }}
        logNoteSource={logNoteSource}
        isClientChartVersion={isClientChartVersion}
      />
    )
  }

  if (showRiskBadge) {
    return <ProfileCardBadge text={isDAProvider ? RISK_BADGE_TEXT : SELF_HARM_BADGE_TEXT} />
  }

  return null
}

export const RiskBadgeWithPopover: React.FC<RiskBadgeWithPopoverProps> = ({
  riskEntry,
  client,
  logNoteSource = 'riskAlerts',
  isClientChartVersion = false,
}) => {
  const { colors } = useTheme() as ThemeType
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const [isOpen, setIsOpen] = useState(false)
  const { key: locationKey } = useLocation()
  useEffect(() => setIsOpen(false), [locationKey])
  const [selectedAlert, setSelectedAlert] = useState<(RiskAlertPopoverAlert & { taskId: string }) | null>(null)
  const triggerRef = useRef(null)
  const { navigateToGeneralNote } = useNavigateToGeneralNote()
  const navigate = useNavigate()
  const providerId = useSelector(getAuthUserId)
  const isDAProvider = useIsEngageDAProvider()
  const isAddressed = riskEntry.status === 'addressed_visible'
  const { data: safetyPlan } = useClientSafetyPlan(isClientChartVersion ? client.id : null)

  const appointmentEpisodeId = client.nextAppointment?.episodeId ?? client.lastAppointment?.episodeId

  const { data: episodes } = useGetClientEpisodesQuery(
    isAddressed && !appointmentEpisodeId ? { patientId: client.id, providerId } : skipToken,
  )
  const episodeId = appointmentEpisodeId ?? episodes?.[0]?.id

  const { data: riskFlagData, isError: riskFlagError } = useGetRiskFlagQuery(
    isAddressed && episodeId ? { episodeId } : skipToken,
  )

  const [deactivateRiskFlag] = useDeactivateRiskFlagMutation()
  const { markRiskAddressed, isLoading: isMarkingAddressed } = useMarkRiskAddressed()

  const alertItem: RiskAlertPopoverAlert & { taskId: string } = useMemo(() => {
    const { id, type, arguments: args } = riskEntry.task
    const isSelfHarm = type === ProviderTaskType.SELF_HARM
    const eventDate = isSelfHarm
      ? parseTaskDate(
          (args as ProviderSelfHarmTaskArguments)?.self_harm_date,
          (args as ProviderSelfHarmTaskArguments)?.self_harm_time,
        )
      : parseTaskDate(
          (args as ProviderTriageRiskTaskArguments)?.event_date,
          (args as ProviderTriageRiskTaskArguments)?.event_time,
        )
    return { taskId: id, taskType: type, taskArguments: args, eventDate }
  }, [riskEntry.task])

  const { data: popoverData } = useRiskAlertPopoverData(isOpen ? client.id : null)

  const handleHideRiskBadge = useCallback(async () => {
    if (episodeId) {
      try {
        await deactivateRiskFlag({ episodeId }).unwrap()
        setIsOpen(false)
      } catch {
        dispatch(
          setToastContent({
            text: intl.formatMessage({
              defaultMessage: 'Failed to hide risk badge. Please try again.',
              description: 'Error message when hiding risk badge fails',
            }),
            id: uniqueId('hide-risk-badge-error'),
            toastType: 'error',
          }),
        )
      }
    }
  }, [episodeId, deactivateRiskFlag, dispatch, intl])

  const handleConfirmMarkAddressed = useCallback(
    async (keepBadgeVisible: boolean) => {
      if (!selectedAlert) return
      const success = await markRiskAddressed(
        { taskId: selectedAlert.taskId, taskType: selectedAlert.taskType, clientId: client.id },
        keepBadgeVisible,
      )
      if (success) setSelectedAlert(null)
    },
    [client.id, markRiskAddressed, selectedAlert],
  )

  if (isAddressed && (riskFlagError || !riskFlagData)) {
    return null
  }

  return (
    <>
      <Pressable
        ref={triggerRef}
        onPress={() => setIsOpen(true)}
        accessibilityRole='button'
        accessibilityLabel={intl.formatMessage(
          {
            defaultMessage: 'View {status} risk details',
            description: 'Accessibility label for risk badge button',
          },
          { status: isAddressed ? 'addressed' : 'active' },
        )}
      >
        <RiskBadgeContainer>
          {isAddressed && <CheckIcon size={16} fillColor={colors.textError} withCircle />}
          <BodyText
            text={<FormattedMessage defaultMessage='Risk' description='Risk badge label' />}
            size={BodyTextSize.BADGE}
            color={colors.textError}
            style={badgeTextStyle}
          />
          <ChevronIcon size={16} fillColor={colors.textError} direction={ChevronIconDirection.RIGHT} />
        </RiskBadgeContainer>
      </Pressable>
      <Popover
        title={
          isDAProvider ? (
            <FormattedMessage defaultMessage='Risk alert' description='Risk alert popover title for DA provider' />
          ) : (
            <FormattedMessage
              defaultMessage='Suicidal ideation and intent'
              description='Risk alert popover title for LCx provider'
            />
          )
        }
        trigger={triggerRef}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        placement='bottom left'
        testIdPrefix='RiskBadgePopover'
        positionKey={popoverData.isLoading ? 'loading' : 'ready'}
        aboveHeaderContent={
          isAddressed ? (
            <AddressedLabel>
              <CheckIcon size={16} fillColor={colors.textSuccess} withCircle />
              <BodyText
                size={BodyTextSize.SMALL}
                color={colors.textSuccess}
                text={<FormattedMessage defaultMessage='Addressed' description='Risk addressed status label' />}
              />
            </AddressedLabel>
          ) : undefined
        }
      >
        <RiskAlertPopover
          alert={alertItem}
          data={popoverData}
          clientId={client.id}
          onMarkAddressed={() => setSelectedAlert(alertItem)}
          onLogNote={() => navigateToGeneralNote(client.id, logNoteSource)}
          isAddressed={isAddressed}
          onHideRiskBadge={handleHideRiskBadge}
          hideAddressedLabel
          isClientChartVersion={isClientChartVersion}
          onReviewRiskSummary={() => navigate(CLIENT_RISK_SUMMARY.route)}
          onSafetyPlanPress={() => navigate(getSafetyPlanRoute(safetyPlan))}
        />
      </Popover>
      <MarkRiskAddressedModal
        visible={selectedAlert !== null}
        onConfirm={handleConfirmMarkAddressed}
        onClose={() => setSelectedAlert(null)}
        confirmLoading={isMarkingAddressed}
      />
    </>
  )
}
